import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PortalLayout from '@/components/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/App';
import { 
  Buildings, 
  Folder,
  FileText,
  Plus,
  ArrowRight,
  Clock,
  CheckCircle,
  Warning,
  Lightning,
  Drop
} from '@phosphor-icons/react';

const PUBLIC_STATUS_COLORS = {
  'New Service Request': 'bg-blue-100 text-blue-700',
  'Quotation Accepted': 'bg-indigo-100 text-indigo-700',
  'Site Visit Scheduled': 'bg-cyan-100 text-cyan-700',
  'Query Logged': 'bg-yellow-100 text-yellow-700',
  'Under Review': 'bg-orange-100 text-orange-700',
  'Approved': 'bg-green-100 text-green-700',
  'Completed': 'bg-emerald-100 text-emerald-700'
};

const PUBLIC_STATUS_ICONS = {
  'New Service Request': Clock,
  'Quotation Accepted': CheckCircle,
  'Site Visit Scheduled': Lightning,
  'Query Logged': Folder,
  'Under Review': Warning,
  'Approved': CheckCircle,
  'Completed': CheckCircle
};

const PortalDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const { data } = await api.get('/dashboard/client');
        setData(data);
      } catch (error) {
        console.error('Dashboard fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#0055FF] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </PortalLayout>
    );
  }

  const activeCases = data?.cases?.filter(c => c.public_status !== 'Approved') || [];
  const completedCases = data?.cases?.filter(c => c.public_status === 'Approved') || [];
  const awaitingInput = data?.cases?.filter(c => c.public_status === 'Under Review') || [];

  return (
    <PortalLayout>
      <div className="space-y-8 fade-in">
        {/* Welcome Banner */}
        <div className="bg-[#0A192F] text-white p-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#0055FF]/20 to-transparent"></div>
          <div className="relative z-10">
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">Welcome to Your Portal</h1>
            <p className="text-gray-300 mb-6">Track your cases and manage your municipal account services.</p>
            <Link to="/portal/service-request">
              <Button className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-none" data-testid="new-request-btn">
                <Plus className="w-4 h-4 mr-2" /> New Service Request
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="dashboard-widget" data-testid="stat-companies">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#0055FF]/10 flex items-center justify-center">
                  <Buildings className="w-6 h-6 text-[#0055FF]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0A192F]">{data?.companies?.length || 0}</p>
                  <p className="text-sm text-[#4B5563]">Companies</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-widget" data-testid="stat-active">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#0055FF]/10 flex items-center justify-center">
                  <Folder className="w-6 h-6 text-[#0055FF]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0A192F]">{activeCases.length}</p>
                  <p className="text-sm text-[#4B5563]">Active Cases</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-widget warning" data-testid="stat-awaiting">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#F59E0B]/10 flex items-center justify-center">
                  <Warning className="w-6 h-6 text-[#F59E0B]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0A192F]">{awaitingInput.length}</p>
                  <p className="text-sm text-[#4B5563]">Action Needed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-widget success" data-testid="stat-completed">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#10B981]/10 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0A192F]">{completedCases.length}</p>
                  <p className="text-sm text-[#4B5563]">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Required */}
        {awaitingInput.length > 0 && (
          <Card className="border-l-4 border-l-[#FF4500]">
            <CardHeader className="border-b border-[#E5E7EB] pb-4">
              <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
                <Warning className="w-5 h-5 text-[#FF4500]" />
                Action Required
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-3">
                {awaitingInput.map((caseItem) => (
                  <Link 
                    key={caseItem.case_id} 
                    to={`/portal/cases/${caseItem.case_id}`}
                    className="block"
                    data-testid={`action-case-${caseItem.case_id}`}
                  >
                    <div className="flex items-center justify-between p-4 border border-[#FF4500]/30 bg-[#FF4500]/5 hover:bg-[#FF4500]/10 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-[#FF4500]/10 flex items-center justify-center">
                          {caseItem.service_type?.includes('Water') ? (
                            <Drop className="w-5 h-5 text-[#FF4500]" />
                          ) : (
                            <Lightning className="w-5 h-5 text-[#FF4500]" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-[#0A192F]">{caseItem.service_type}</p>
                          <p className="text-sm text-[#4B5563]">We need information from you</p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-[#FF4500]" />
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Cases */}
        <Card>
          <CardHeader className="border-b border-[#E5E7EB] pb-4 flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
              <Folder className="w-5 h-5 text-[#0055FF]" />
              Your Cases
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {data?.cases && data.cases.length > 0 ? (
              <div className="space-y-3">
                {data.cases.map((caseItem) => {
                  const StatusIcon = PUBLIC_STATUS_ICONS[caseItem.public_status] || Clock;
                  return (
                    <Link 
                      key={caseItem.case_id} 
                      to={`/portal/cases/${caseItem.case_id}`}
                      className="block"
                      data-testid={`case-${caseItem.case_id}`}
                    >
                      <div className="flex items-center justify-between p-4 border border-[#E5E7EB] hover:border-[#0055FF] transition-colors group">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-[#0055FF]/10 flex items-center justify-center">
                            {caseItem.service_type?.includes('Water') ? (
                              <Drop className="w-5 h-5 text-[#0055FF]" />
                            ) : (
                              <Lightning className="w-5 h-5 text-[#0055FF]" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-[#0A192F] group-hover:text-[#0055FF] transition-colors">
                              {caseItem.service_type}
                            </p>
                            <p className="text-sm text-[#4B5563]">
                              {new Date(caseItem.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={PUBLIC_STATUS_COLORS[caseItem.public_status] || 'bg-gray-100'}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {caseItem.public_status}
                          </Badge>
                          <ArrowRight className="w-5 h-5 text-[#4B5563] group-hover:text-[#0055FF] transition-colors" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Folder className="w-12 h-12 text-[#D1D5DB] mx-auto mb-4" />
                <p className="text-[#4B5563] mb-4">No cases yet</p>
                <Link to="/portal/service-request">
                  <Button className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none">
                    <Plus className="w-4 h-4 mr-2" /> Submit a Request
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Companies */}
        {data?.companies && data.companies.length > 0 && (
          <Card>
            <CardHeader className="border-b border-[#E5E7EB] pb-4">
              <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
                <Buildings className="w-5 h-5 text-[#0055FF]" />
                Your Companies
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid md:grid-cols-2 gap-4">
                {data.companies.map((company) => (
                  <div key={company.company_id} className="p-4 border border-[#E5E7EB]" data-testid={`company-${company.company_id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#0055FF]/10 flex items-center justify-center">
                        <Buildings className="w-5 h-5 text-[#0055FF]" />
                      </div>
                      <div>
                        <p className="font-medium text-[#0A192F]">{company.company_name}</p>
                        {company.physical_address && (
                          <p className="text-sm text-[#4B5563] mt-1">{company.physical_address}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contracts */}
        {data?.contracts && data.contracts.length > 0 && (
          <Card>
            <CardHeader className="border-b border-[#E5E7EB] pb-4">
              <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#0055FF]" />
                Your Contracts
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-3">
                {data.contracts.map((contract) => (
                  <div key={contract.contract_id} className="flex items-center justify-between p-4 border border-[#E5E7EB]">
                    <div>
                      <p className="font-medium text-[#0A192F]">{contract.contract_type}</p>
                      <p className="text-sm text-[#4B5563]">
                        Valid until {new Date(contract.end_date).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge className={contract.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100'}>
                      {contract.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PortalLayout>
  );
};

export default PortalDashboard;
